import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

type Theme = "light" | "dark";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // index.html's inline script has already resolved the initial theme
  const [theme, setThemeState] = useState<Theme>(() =>
    document.documentElement.classList.contains("dark") ? "dark" : "light"
  );

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem("theme", t);
    document.documentElement.classList.toggle("dark", t === "dark");
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(
      document.documentElement.classList.contains("dark") ? "light" : "dark"
    );
  }, [setTheme]);

  useEffect(() => {
    // follow live system changes only while the user has no explicit choice
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem("theme")) return;
      const t: Theme = e.matches ? "dark" : "light";
      setThemeState(t);
      document.documentElement.classList.toggle("dark", t === "dark");
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function RuntimeThemeProvider({ theme, children }: { theme: Theme; children: React.ReactNode }) {
  const [currentTheme, setThemeState] = useState<Theme>(theme);
  useEffect(() => {
    setThemeState(theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);
  const setTheme = useCallback((value: Theme) => {
    setThemeState(value);
    document.documentElement.classList.toggle("dark", value === "dark");
  }, []);
  const toggleTheme = useCallback(() => setTheme(currentTheme === "dark" ? "light" : "dark"), [currentTheme, setTheme]);
  return <ThemeContext.Provider value={{ theme: currentTheme, setTheme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
